import React from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ABC_TOOLTIP,
  ANALYSIS_PERIOD_TOOLTIP,
  CALCULATION_GRANULARITY_TOOLTIP,
  CROSS_TOOLTIP,
  CV_TOOLTIP_LINES,
  OBJECT_MATRIX_TOOLTIP,
  TOTAL_METRIC_VALUE_TOOLTIP,
  VARIANT_MATRIX_TOOLTIP,
  XYZ_TOOLTIP,
} from "../constants";

const SECTION_CONTENT = {
  variantMatrix: { heading: "Variant Matrix", text: VARIANT_MATRIX_TOOLTIP },
  objectMatrix: { heading: "Object Matrix", text: OBJECT_MATRIX_TOOLTIP },
  abc: { heading: "ABC — Impact", text: ABC_TOOLTIP },
  xyz: { heading: "XYZ — Stability", text: XYZ_TOOLTIP },
  cv: { lines: CV_TOOLTIP_LINES },
  totalMetricValue: {
    heading: "Total Metric Value",
    text: TOTAL_METRIC_VALUE_TOOLTIP,
  },
  analysisPeriod: {
    heading: "Analysis Period",
    text: ANALYSIS_PERIOD_TOOLTIP,
  },
  calculationGranularity: {
    heading: "Calculation Granularity",
    text: CALCULATION_GRANULARITY_TOOLTIP,
  },
  cross: { heading: "Cross Matrix", text: CROSS_TOOLTIP },
};

const renderLine = ({ label, text }) => (
  <div key={label} className="text-xs leading-normal text-gray-500">
    <span className="font-medium text-gray-600">{label}:</span> {text}
  </div>
);

const DefaultTrigger = React.forwardRef(({ title, ...triggerProps }, ref) => (
  <button
    ref={ref}
    type="button"
    className="inline-flex text-gray-400 hover:text-gray-500 transition-colors"
    aria-label={`${title} help`}
    {...triggerProps}
  >
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
        clipRule="evenodd"
      />
    </svg>
  </button>
));
DefaultTrigger.displayName = "DefaultTrigger";

const ABCXYZInfoTooltip = ({
  sections = ["abc", "xyz", "cv"],
  title = "ABC/XYZ Analysis",
  extraLines = null,
  trigger = null,
}) => (
  <Tooltip.Provider delayDuration={200}>
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        {trigger || <DefaultTrigger title={title} />}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          align="start"
          sideOffset={6}
          className="max-w-[280px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
        >
          <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
            <span className="text-xs font-medium text-primary-700">
              {title}
            </span>
          </div>
          <div className="px-2.5 py-1.5 space-y-1.5">
            {sections.map((key) => {
              const section = SECTION_CONTENT[key];
              if (section.lines) {
                return (
                  <div key={key} className="space-y-1">
                    {section.lines.map(renderLine)}
                  </div>
                );
              }
              return (
                <div key={key} className="text-xs leading-normal text-gray-500">
                  <span className="font-medium text-gray-600">
                    {section.heading}:
                  </span>{" "}
                  {section.text}
                </div>
              );
            })}
            {extraLines && (
              <div className="space-y-1">{extraLines.map(renderLine)}</div>
            )}
          </div>
          <Tooltip.Arrow className="fill-white" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  </Tooltip.Provider>
);

export default ABCXYZInfoTooltip;
